import { useMemo } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Button,
  Checkbox,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@clouway/ui'

const SENSOR_UNITS = ['percent', 'celsius', 'volt', 'status'] as const

// Form shape, decoupled from the wire SensorConfig — id (the AVL port 0–3) and
// multiplier are strings here; the tab maps them to numbers.
const buildSchema = (requiredMessage: string) =>
  z.object({
    id: z.string().min(1, requiredMessage),
    title: z.string().min(1, requiredMessage),
    unit: z.string().min(1, requiredMessage),
    multiplier: z.string(),
    enabled: z.boolean(),
    inverse: z.boolean(),
  })

export type SensorFormValues = z.infer<ReturnType<typeof buildSchema>>

export function SensorEditForm({
  availablePorts,
  showInverse,
  initialValues,
  onSubmit,
  onCancel,
}: {
  availablePorts: string[]
  showInverse: boolean
  initialValues?: SensorFormValues
  onSubmit: SubmitHandler<SensorFormValues>
  onCancel: () => void
}) {
  const { t } = useTranslation('feature')
  const schema = useMemo(() => buildSchema(t('sensorForm.required')), [t])
  const { control, handleSubmit, formState } = useForm<SensorFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ?? { id: '', title: '', unit: '', multiplier: '', enabled: true, inverse: false },
  })

  return (
    <form className="flex flex-col gap-4 rounded-md border p-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="id"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('sensorForm.port')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={t('sensorForm.port')}>
                <SelectValue placeholder={t('sensorForm.port')} />
              </SelectTrigger>
              <SelectContent>
                {availablePorts.map((port) => (
                  <SelectItem key={port} value={port}>
                    {port}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="title"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="sensor-title">{t('sensorForm.type')}</FieldLabel>
            <Input id="sensor-title" {...field} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="unit"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('sensorForm.unit')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={t('sensorForm.unit')}>
                <SelectValue placeholder={t('sensorForm.unit')} />
              </SelectTrigger>
              <SelectContent>
                {SENSOR_UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {t(`trackingObjects.config.sensorInputUnits.${unit}` as 'trackingObjects.config.sensorInputUnits.percent')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="multiplier"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="sensor-multiplier">{t('sensorForm.multiplier')}</FieldLabel>
            <Input id="sensor-multiplier" type="number" step="any" {...field} />
          </Field>
        )}
      />

      <Controller
        control={control}
        name="enabled"
        render={({ field }) => (
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />
            {t('sensorForm.active')}
          </label>
        )}
      />

      {showInverse && (
        <Controller
          control={control}
          name="inverse"
          render={({ field }) => (
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />
              {t('sensorForm.inverse')}
            </label>
          )}
        />
      )}

      <div className="flex justify-between gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('sensorForm.cancel')}
        </Button>
        <Button type="submit" disabled={formState.isSubmitting}>
          {formState.isSubmitting ? t('sensorForm.submitting') : t('sensorForm.submit')}
        </Button>
      </div>
    </form>
  )
}
